// SPDX-FileCopyrightText: © 2021 Olivier Meunier <olivier@neokraft.net>
//
// SPDX-License-Identifier: AGPL-3.0-only

import {Controller} from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  static override targets = ["embed", "play"]

  declare readonly embedTarget: HTMLTemplateElement
  declare readonly playTarget: HTMLTemplateElement

  declare media: HTMLIFrameElement | HTMLVideoElement | null
  declare playBtn: HTMLButtonElement | null

  override connect() {
    // Local downloaded videos ship as a bare <video> element so the click and
    // the .play() call happen in the same document — Safari's per-site
    // Auto-Play preference otherwise vetoes playback started from a
    // just-mounted iframe. Third-party embeds still ship as <iframe> for CSP
    // isolation.
    this.media = this.embedTarget.content.querySelector("iframe, video")
    if (!this.media) {
      return
    }

    if (this.media.tagName === "IFRAME") {
      this.media.setAttribute("sandbox", "allow-scripts allow-same-origin")
    }

    const w = parseInt(this.media.getAttribute("width") || "") || 0
    const h = parseInt(this.media.getAttribute("height") || "") || 0

    if (w > 0 && h > 0) {
      this.element.style.aspectRatio = `${w} / ${h}`
    }

    this.playBtn = this.playTarget.content.querySelector("button")
    if (!this.playBtn) {
      return
    }
    this.element.appendChild(this.playBtn)
  }

  play() {
    if (!this.media || !this.playBtn) {
      return
    }

    this.playBtn.remove()
    // Remove the poster <img>. It's only there to show a preview under the
    // Play button; once the media is mounted it can only cause subpixel
    // bleed at the edges of whichever element is on top.
    const poster = this.element.querySelector(":scope > img")
    if (poster) {
      poster.remove()
    }
    this.element.appendChild(this.media)
    // Synchronous .play() inside the click handler is what makes Safari treat
    // this as a direct user gesture on the media element instead of autoplay.
    // Chrome/Firefox honor it too; both silently reject via the returned
    // promise if the browser still refuses — nothing else to do there.
    if (this.media instanceof HTMLVideoElement) {
      this.media.play().catch(() => {})
    }
  }
}
